"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.TaskRepository = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const constants_1 = require("../../../../../../libs/service-lib/src/lib/constants");
const messages_1 = require("../../../../../../libs/service-lib/src/lib/messages");
const entity_service_utils_1 = require("../../../../../../libs/service-lib/src/lib/utils/entity-service.utils");
const constants_2 = require("../../../../service-lib/src/lib/constants");
const entities_1 = require("../../../../service-lib/src/lib/entities");
let TaskRepository = class TaskRepository {
    constructor(taskRepository, entityService) {
        this.taskRepository = taskRepository;
        this.entityService = entityService;
    }
    async createTask(taskDetails, manager) {
        try {
            // Task linking validation
            await this.validateTaskAssociations(manager, taskDetails.companyId, taskDetails.opportunityId, taskDetails.activityId);
            const task = manager.create(entities_1.Task, taskDetails);
            return await manager.save(entities_1.Task, task);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error creating task: " + error.message);
        }
    }
    async createTaskDocuments(documents, taskId, manager) {
        try {
            await Promise.all(documents.map(async (doc) => {
                if (doc.documentId) {
                    const document = await manager.findOne(entities_1.FileUpload, {
                        where: { id: doc.documentId },
                    });
                    if (!document) {
                        throw new common_1.NotFoundException(messages_1.errorMessages.documentWithIdNotFound(doc.documentId));
                    }
                }
            }));
            const taskDocuments = documents.map((doc) => ({
                taskId: taskId,
                documentId: doc.documentId,
            }));
            await manager.save(entities_1.TaskDocumentMap, taskDocuments);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error creating task document: " + error.message);
        }
    }
    async updateTask(id, updateTask, manager, fromActivity) {
        try {
            const task = await this.taskRepository.findOne({ where: { id } });
            if (!task) {
                throw new common_1.NotFoundException(messages_1.errorMessages.taskWithIdNotFound(id));
            }
            if (task.taskIsEditable == constants_1.IS_NOT_EDITABLE && !fromActivity) {
                throw new common_1.BadRequestException(`Task with ID ${id} is not editable as it is activity related task.`);
            }
            // Validate associations before updating
            await this.validateTaskAssociations(manager, updateTask.companyId, updateTask.opportunityId, updateTask.activityId);
            await manager.update(entities_1.Task, { id }, updateTask);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error updating task: " + error.message);
        }
    }
    async updateTaskDocuments(documents, taskId, manager) {
        try {
            const existingDocuments = await manager.find(entities_1.TaskDocumentMap, {
                where: { taskId },
            });
            const existingDocumentIds = existingDocuments.map((doc) => Number(doc.documentId));
            // Add new documents
            const newDocuments = documents.filter((doc) => doc.documentId && !existingDocumentIds.includes(doc.documentId));
            if (newDocuments.length > 0) {
                await this.createTaskDocuments(newDocuments, taskId, manager);
            }
            // Remove documents that are not in the documents array
            const documentsToRemove = existingDocuments.filter((doc) => !documents.some((d) => d.documentId === doc.documentId));
            if (documentsToRemove.length > 0) {
                await manager.remove(entities_1.TaskDocumentMap, documentsToRemove);
            }
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error updating task document: " + error.message);
        }
    }
    async completeTask(id, manager) {
        try {
            const task = await this.taskRepository.findOne({ where: { id } });
            if (!task) {
                throw new common_1.NotFoundException(messages_1.errorMessages.taskWithIdNotFound(id));
            }
            const subTaskType = await manager.findOne(entities_1.LookUp, {
                where: { lookUpKey: constants_2.TASK_TYPE.TASK },
            });
            if (!subTaskType) {
                throw new common_1.NotFoundException(`Task type with key ${constants_2.TASK_TYPE.TASK} not found`);
            }
            if (task.taskTypeLid !== subTaskType.id) {
                throw new common_1.BadRequestException(messages_1.errorMessages.invalidTaskType);
            }
            const closedStatus = await manager.findOne(entities_1.LookUp, {
                where: { lookUpKey: constants_2.TASK_STATUS_CLOSED },
            });
            const activeStatus = await manager.findOne(entities_1.LookUp, {
                where: { lookUpKey: constants_2.TASK_STATUS_ACTIVE },
            });
            if (!closedStatus || !activeStatus) {
                throw new common_1.NotFoundException(`Task status with key ${constants_2.TASK_STATUS_CLOSED} or ${constants_2.TASK_STATUS_ACTIVE} not found`);
            }
            if (task.taskStatusLid === activeStatus?.id) {
                // update to closed
                await manager.update(entities_1.Task, { id }, { taskStatusLid: closedStatus.id, taskClose: constants_2.TASK_CLOSED });
            }
            else if (task.taskStatusLid === closedStatus?.id) {
                // already closed, reopen.
                await manager.update(entities_1.Task, { id }, { taskStatusLid: activeStatus.id, taskClose: null });
            }
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error completing task: " + error.message);
        }
    }
    async getTaskById(id) {
        try {
            const task = await this.taskRepository.findOne({
                where: { id },
                relations: [
                    "company",
                    "assignee",
                    "opportunity",
                    "opportunity.policyType",
                    "activity",
                    "taskDocs",
                ],
            });
            if (!task) {
                throw new common_1.NotFoundException(messages_1.errorMessages.taskNotFound);
            }
            const lookUpValues = await this.entityService.getLookupValues([
                task.priorityLid,
                task.taskStatusLid,
                task.taskTypeLid,
            ]);
            if (!lookUpValues) {
                throw new Error("Failed to fetch lookup values");
            }
            // Map the lookup values to their respective fields
            const priority = lookUpValues.find((item) => item.id === task.priorityLid);
            const taskStatus = lookUpValues.find((item) => item.id === task.taskStatusLid);
            const taskType = lookUpValues.find((item) => item.id === task.taskTypeLid);
            // Spread the lookup values into the task object
            const enrichedTask = {
                ...task,
                priority,
                taskStatus,
                taskType,
                // taskCategory,
            };
            // Validate and filter the response
            const filteredData = await this.transformTaskResponse([enrichedTask]);
            return filteredData[0];
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error fetching task: " + error.message);
        }
    }
    async deleteTaskById(id) {
        try {
            const task = await this.taskRepository.findOne({ where: { id } });
            if (!task) {
                throw new common_1.NotFoundException(messages_1.errorMessages.taskWithIdNotFound(id));
            }
            await this.taskRepository.softDelete({ id });
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error deleting task: " + error.message);
        }
    }
    async getTasks(page, limit, searchArray, sort, userId, searchBy, statusId) {
        try {
            const { data, count } = await this.entityService.fetchEntityList(entities_1.Task, page, limit, sort.length > 0 ? sort : [{ field: "id", order: "ASC" }], [
                "company",
                "assignee",
                "opportunity",
                "opportunity.policyType",
                "activity",
            ], {
                createdBy: userId,
                ...(statusId ? { taskStatusLid: statusId } : {}),
            }, undefined, searchArray.length === 0 ? [] : searchArray, undefined, searchBy, constants_2.taskSearchObject);
            if (!data || data.length === 0) {
                return { data: [], count: 0 };
            }
            // Extract all unique priorityLid values from the data
            const priorityLids = [
                ...new Set(data.map((task) => task.priorityLid).filter(Boolean)),
            ];
            const taskStatusLids = [
                ...new Set(data.map((task) => task.taskStatusLid).filter(Boolean)),
            ];
            const taskTypeLids = [
                ...new Set(data.map((task) => task.taskTypeLid).filter(Boolean)),
            ];
            const allLids = [
                ...new Set([...priorityLids, ...taskStatusLids, ...taskTypeLids]),
            ];
            // Fetch lookup values for all LIDs in one call
            const allLookupValues = await this.entityService.getLookupValues(allLids);
            const priorityLookup = allLookupValues.filter((item) => priorityLids.includes(item.id));
            const taskStatusLookup = allLookupValues.filter((item) => taskStatusLids.includes(item.id));
            const taskTypeLookup = allLookupValues.filter((item) => taskTypeLids.includes(item.id));
            return {
                data: await this.transformGetTasksResponse(data, priorityLookup, taskStatusLookup, taskTypeLookup),
                count,
            };
        }
        catch (error) {
            throw new Error("Failed to fetch get tasks list", error);
        }
    }
    async getTasksByOpportunityAndActivity(opportunityId, activityId) {
        try {
            const tasks = await this.taskRepository.find({
                where: {
                    opportunityId,
                    activityId,
                },
                relations: ["assignee", "priority", "company"], // Ensure relations are properly loaded
            });
            const transformedTasks = tasks.map((task) => ({
                id: task.id,
                taskName: task.taskName,
                description: task.description,
                dueDate: task.dueDate,
                priority: {
                    id: task.priority?.id,
                    lookUpValue: task.priority?.lookUpValue,
                },
                company: {
                    id: task.company?.id,
                    name: task.company?.companyName,
                },
                assignee: {
                    id: task.assignee?.userId,
                    name: task.assignee?.firstName,
                },
            }));
            return transformedTasks;
        }
        catch (error) {
            throw new Error("Error fetching tasks by opportunity and activity: " + error.message);
        }
    }
    async transformTaskResponse(tasks) {
        try {
            return tasks.map((task) => ({
                ...this.omitFields(task),
                priorityLid: undefined,
                taskStatusLid: undefined,
                taskTypeLid: undefined,
                taskDocs: task?.taskDocs?.map((doc) => ({
                    documentId: doc.documentId,
                })),
                companyId: undefined,
                company: task?.company
                    ? {
                        id: task.company.id,
                        name: task.company.companyName,
                        displayName: task.company.displayName,
                    }
                    : undefined,
                opportunityId: undefined,
                opportunity: task?.opportunity
                    ? {
                        opportunityId: task.opportunity.opportunityId,
                        policy: task.opportunity.policyType?.lookUpValue,
                    }
                    : undefined,
                assigneeId: undefined,
                assignee: task?.assignee
                    ? {
                        userId: task.assignee.userId,
                        firstName: task.assignee.firstName,
                        lastName: task.assignee.lastName,
                    }
                    : undefined,
                activityId: undefined,
                activity: task?.activity
                    ? {
                        id: task.activity.id,
                        activityName: task.activity.activityName,
                    }
                    : undefined,
            }));
        }
        catch (error) {
            throw new Error("Failed to transform task response", error);
        }
    }
    // Transform the response for getTasks
    async transformGetTasksResponse(tasks, priorityLookup, taskStatusLookup, taskTypeLookup) {
        try {
            return tasks.map((task) => ({
                ...this.omitFields(task),
                priorityLid: undefined,
                taskStatusLid: undefined,
                taskTypeLid: undefined,
                taskClose: undefined,
                taskDocs: task?.taskDocs?.map((doc) => ({
                    documentId: doc.documentId,
                })),
                companyId: undefined,
                company: task?.company
                    ? {
                        id: task.company.id,
                        name: task.company.companyName,
                        displayName: task.company.displayName,
                    }
                    : undefined,
                opportunityId: undefined,
                opportunity: task?.opportunity
                    ? {
                        opportunityId: task.opportunity.opportunityId,
                        policy: task.opportunity.policyType?.lookUpValue,
                    }
                    : undefined,
                assigneeId: undefined,
                assignee: task?.assignee
                    ? {
                        userId: task.assignee.userId,
                        firstName: task.assignee.firstName,
                        lastName: task.assignee.lastName,
                    }
                    : undefined,
                activityId: undefined,
                activity: task?.activity
                    ? {
                        id: task.activity.id,
                        activityName: task.activity.activityName,
                    }
                    : undefined,
                priority: {
                    id: task.priorityLid,
                    lookUpValue: priorityLookup.find((item) => item.id === task.priorityLid)
                        ?.lookUpValue ?? null,
                },
                taskStatus: {
                    id: task.taskStatusLid,
                    lookUpValue: taskStatusLookup.find((item) => item.id === task.taskStatusLid)
                        ?.lookUpValue ?? null,
                },
                taskType: {
                    id: task.taskTypeLid,
                    lookUpValue: taskTypeLookup.find((item) => item.id === task.taskTypeLid)
                        ?.lookUpValue ?? null,
                },
            }));
        }
        catch (error) {
            throw new Error("Failed to transform task response", error);
        }
    }
    omitFields(entity) {
        if (!entity) {
            return {};
        }
        const { createdAt, updatedAt, deletedAt, createdBy, updatedBy, ...rest } = entity || {};
        return rest;
    }
    // Validate associations for task creation or update
    async validateTaskAssociations(entityManager, companyId, opportunityId, activityId) {
        if (companyId) {
            const company = await entityManager.findOne(entities_1.Company, {
                where: { id: companyId },
            });
            if (!company) {
                throw new common_1.NotFoundException(messages_1.errorMessages.companyNotFound);
            }
        }
        if (opportunityId) {
            const opportunity = await entityManager.findOne(entities_1.Opportunity, {
                where: { opportunityId: opportunityId },
            });
            if (!opportunity) {
                throw new common_1.NotFoundException(messages_1.errorMessages.opportunityNotFound);
            }
        }
        if (opportunityId && activityId) {
            const activities = await entityManager.find(entities_1.OpportunityActivityMap, {
                where: { opportunityId: opportunityId },
            });
            if (!activities) {
                throw new common_1.BadRequestException("No activities found for the given opportunity ID");
            }
            const activityExists = activities.some((activity) => activity.id === activityId);
            if (!activityExists) {
                throw new common_1.BadRequestException(`Activity ID ${activityId} not found for the given opportunity ID`);
            }
        }
        else if (!opportunityId && activityId) {
            throw new common_1.BadRequestException("Activity ID provided without an associated Opportunity ID");
        }
    }
};
exports.TaskRepository = TaskRepository;
exports.TaskRepository = TaskRepository = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(entities_1.Task)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof entity_service_utils_1.EntityService !== "undefined" && entity_service_utils_1.EntityService) === "function" ? _b : Object])
], TaskRepository);
